import { PROJECT_BATCH_IMAGES } from "@/data/projectBatchImages";
import useProjectBatchPhotos from "@/hooks/useProjectBatchPhotos";
import { motion } from "framer-motion";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import QuoteModal from "@/components/QuoteModal";
import PageBreadcrumb from "@/components/PageBreadcrumb";
import ProjectSpotlights from "@/components/ProjectSpotlights";
import subfloorHeroImg from "@/assets/needle-rolling-self-leveling-underlayment-1-wood-floors-northville.webp";

import primerMastic from "@/assets/subfloor-bonding-primer-over-mastic-livonia-mi.webp";
import sluPour from "@/assets/engineered-self-leveling-underlayment-concrete-livonia.webp";
import hickoryFinished from "@/assets/custom-8-inch-hickory-hardwood-michigan-plank-fireside.webp";
import bareSubfloor from "@/assets/bare-plywood-subfloor-evaluation-1-wood-floors-northville-mi.webp";
import bluePrimer from "@/assets/specialized-blue-subfloor-primer-1-wood-floors.webp";
import needleRoller from "@/assets/needle-rolling-self-leveling-underlayment-1-wood-floors-northville.webp";
const joistRepairImg = "/images/site/floor-joist-repair-structural-dip-correction.webp";
const hardShimImg = "/images/site/subfloor-hard-shim-plywood-low-spot-correction.webp";
import usePageSEO from "@/hooks/usePageSEO";
import PageFAQ from "@/components/PageFAQ";
import { subfloorFAQs } from "@/data/faqData";
import RelatedServices from "@/components/RelatedServices";
import GoogleReviewsCarousel from "@/components/GoogleReviewsCarousel";
import FullServiceCallout from "@/components/FullServiceCallout";

const galleryPhotos = [
  {
    img: primerMastic,
    alt: PROJECT_BATCH_IMAGES["/subfloor-leveling"].photos[3].alt,
    title: "Specialized Subfloor Primer Over Concrete Mastic Livonia MI",
    caption: "Bonding Primer Over Mastic",
    location: "Livonia, Michigan",
  },
  {
    img: sluPour,
    alt: PROJECT_BATCH_IMAGES["/subfloor-leveling"].photos[4].alt,
    title: "Engineered Self Leveling Underlayment Pour Livonia MI",
    caption: "Engineered SLU Pour  Glass Finish",
    location: "Livonia, Michigan",
  },
  {
    img: hickoryFinished,
    alt: PROJECT_BATCH_IMAGES["/subfloor-leveling"].photos[5].alt,
    title: "Custom 8 Inch Hickory Hardwood Floor Fireside Color",
    caption: "Custom 8″ Hickory  Michigan Plank Fireside",
    location: "Livonia, Michigan",
  },
  {
    img: bareSubfloor,
    alt: PROJECT_BATCH_IMAGES["/subfloor-leveling"].photos[6].alt,
    title: "Bare Plywood Subfloor Evaluation 1 Wood Floors Northville MI",
    caption: "Bare Subfloor Evaluation",
    location: "Northville, Michigan",
  },
  {
    img: bluePrimer,
    alt: PROJECT_BATCH_IMAGES["/subfloor-leveling"].photos[7].alt,
    title: "Specialized Blue Subfloor Bonding Primer 1 Wood Floors",
    caption: "Specialized Blue Bonding Primer",
    location: "Northville, Michigan",
  },
  {
    img: needleRoller,
    alt: PROJECT_BATCH_IMAGES["/subfloor-leveling"].photos[8].alt,
    title: "Needle Rolling Self Leveling Underlayment 1 Wood Floors Northville",
    caption: "Needle Rolling SLU  The Money Shot",
    location: "Northville, Michigan",
  },
];

const fade = { initial: { opacity: 0, y: 28 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

const methods = [
  {
    num: "01",
    title: "Carpentry & Joist Repair",
    body: "Sometimes the problem starts at the bones. We remove the subfloor to plane or sister uneven joists. Resolving severe structural dips and crowns at the source, not just masking them.",
    image: joistRepairImg,
    alt: PROJECT_BATCH_IMAGES["/subfloor-leveling"].photos[1].alt,
  },
  {
    num: "02",
    title: "Mechanical Grinding",
    body: "Swollen OSB seams, high plywood joints, concrete peaks. We aggressively sand them down to tolerance. It's unglamorous work, but it's the difference between a floor that lasts and one that squeaks within a year.",
  },
  {
    num: "03",
    title: "The Hard Shim Method",
    body: "A highly specialized carpentry technique we use to permanently build up low spots with layered plywood, wood shims, and elastomeric glue. Without adding the massive water weight of cementitious patches.",
    image: hardShimImg,
    alt: PROJECT_BATCH_IMAGES["/subfloor-leveling"].photos[2].alt,
  },
  {
    num: "04",
    title: "Advanced SLU (Self-Leveling)",
    body: "Precision-poured, high-tech self-leveling compounds that create a perfectly flat, glass-like foundation. This is our go-to for large-scale corrections. When executed properly, it's the gold standard.",
  },
];

const SubfloorLeveling = () => {
  useProjectBatchPhotos("/subfloor-leveling");
  usePageSEO({
    title: "Subfloor Repair & Leveling | Self-Leveling Underlayment",
    description: "Subfloor repair, leveling, and self-leveling underlayment for hardwood floor preparation. Laser-topography precision, 1/8-inch flatness, structural repair.",
    path: "/subfloor-leveling",
    image: PROJECT_BATCH_IMAGES["/subfloor-leveling"].share,
    // Service schema baked into static HTML via generate-seo-html.mjs
  });

  return (
  <div className="bg-background text-foreground">
    <SiteHeader />
    <main>

    {/* ─── 1 · HERO ─── */}
    <section className="relative h-[100svh] min-h-[560px] max-h-[900px] flex items-end overflow-hidden">
      <PageBreadcrumb variant="overlay" items={[
        { label: "Home", to: "/" },
        { label: "Services" },
        { label: "Subfloor Leveling" },
      ]} />
      <div className="absolute inset-0">
        <img width={1600} height={1067} loading="eager" decoding="async" fetchPriority="high" src={subfloorHeroImg} alt={PROJECT_BATCH_IMAGES["/subfloor-leveling"].photos[0].alt} className="w-full h-full object-cover" />
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-foreground/85 via-foreground/40 to-foreground/10" />

      <motion.div {...fade} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }} className="relative z-10 pb-10 md:pb-16 px-5 md:px-10 lg:px-16 max-w-4xl">
        <p className="text-[10px] md:text-[11px] font-semibold tracking-[.14em] uppercase text-primary-foreground/50 mb-3">
          NWFA Certified &nbsp;·&nbsp; Metro Detroit
        </p>
        <h1 className="font-display text-[clamp(36px,8vw,90px)] font-semibold leading-[1.05] tracking-tight text-primary-foreground">
            Subfloor Leveling and Repair  Built for Michigan Homes
          </h1>
        <p className="mt-4 md:mt-6 text-[13px] md:text-[15px] font-light text-primary-foreground/55 max-w-lg leading-relaxed">
          A premium floor is only as good as its foundation. We engineer the invisible work that most contractors skip. Laser-precision flatness, structural correction, and moisture mitigation for Metro Detroit homes.
        </p>
        <div className="mt-6 md:mt-8">
          <QuoteModal
            trigger={
              <button className="bg-primary text-primary-foreground px-6 py-3 rounded-full text-sm font-medium hover:bg-[hsl(var(--gold-400))] transition-colors">
                Request a Subfloor Evaluation
              </button>
            }
          />
        </div>
      </motion.div>
    </section>

    {/* ─── 2 · FLAT vs LEVEL ─── */}
    <section className="bg-background py-16 md:py-24 lg:py-32">
      <div className="max-w-[1200px] mx-auto px-5 md:px-12 grid lg:grid-cols-2 gap-10 lg:gap-20 items-start">
        <motion.div {...fade}>
          <p className="text-[11px] font-semibold tracking-[.18em] uppercase text-primary mb-2">The NWFA Standard</p>
          <h2 className="font-display text-[clamp(28px,5vw,48px)] font-semibold text-foreground leading-[1.1]">
            The Critical Difference Between <em className="italic font-normal text-primary">Flat and Level.</em>
          </h2>
        </motion.div>
        <motion.div {...fade} transition={{ delay: 0.1 }} className="flex flex-col gap-5">
          <p className="text-[13px] md:text-[15px] font-light text-muted-foreground leading-relaxed">
            There is a massive difference between a floor being <strong className="text-foreground font-medium">level</strong> and a floor being <strong className="text-foreground font-medium">flat</strong>. An older home may slope slightly  and that's okay. But to successfully install hardwood or premium LVP, the subfloor must be perfectly flat. According to NWFA guidelines, that means within <strong className="text-foreground font-medium">1/8 of an inch over a 6-foot span</strong>, or <strong className="text-foreground font-medium">3/16 of an inch over 10 feet</strong>.
          </p>
          <p className="text-[13px] md:text-[15px] font-light text-muted-foreground leading-relaxed border-l-2 border-primary/40 pl-5">
            If your subfloor has dips, crowns, or peaks outside those tolerances, your new flooring will flex, squeak, and eventually fail. We use precision 10-foot straight edges and advanced lasers to verify every square foot before a single plank is laid.
          </p>
        </motion.div>
      </div>
    </section>

    {/* ─── 3 · STRUCTURAL INTEGRITY ─── */}
    <section className="bg-[hsl(var(--neutral-950))] text-primary-foreground py-16 md:py-24 lg:py-32">
      <div className="max-w-[1200px] mx-auto px-5 md:px-12">
        <motion.div {...fade} className="max-w-3xl mb-10">
          <p className="text-[11px] font-semibold tracking-[.18em] uppercase text-[hsl(var(--gold-400))] mb-2">Structural Engineering</p>
          <h2 className="font-display text-[clamp(28px,5vw,48px)] font-semibold leading-[1.1]">
            More Than Surface Level: <em className="italic font-normal text-[hsl(var(--gold-300))]">Structural Integrity &amp; Deflection.</em>
          </h2>
        </motion.div>
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
          <motion.p {...fade} className="text-[13px] md:text-[15px] font-light text-primary-foreground/55 leading-relaxed">
            A perfectly flat floor requires a structurally sound foundation. Before any leveling begins, we evaluate joist spacing and subfloor thickness to ensure they meet NWFA deflection standards. We also calculate point loads  the physical weight your structure can safely handle.
          </motion.p>
          <motion.div {...fade} transition={{ delay: 0.1 }} className="bg-primary-foreground/[.04] border border-primary-foreground/10 rounded-2xl p-6 md:p-8">
            <p className="text-[10px] font-bold tracking-[.14em] uppercase text-[hsl(var(--gold-400))] mb-3">⚠ Engineering Warning</p>
            <p className="text-[13px] md:text-[15px] font-light text-primary-foreground/60 leading-relaxed">
              Pouring self-leveling underlayment adds massive "dead load" to your home's framing. Standard contractors often pour thousands of pounds of cement without checking joist capacity  risking severe structural sagging. <strong className="text-primary-foreground font-medium">We calculate the exact weight loads</strong>, and for complex projects, we collaborate directly with licensed Structural Engineers to guarantee your home remains safe and stable.
            </p>
          </motion.div>
        </div>
      </div>
    </section>

    {/* ─── 4 · FOUR METHODS ─── */}
    <section className="bg-background py-16 md:py-24 lg:py-32">
      <div className="max-w-[1200px] mx-auto px-5 md:px-12">
        <motion.div {...fade} className="mb-10 md:mb-14">
          <p className="text-[11px] font-semibold tracking-[.18em] uppercase text-primary mb-2">Our Arsenal</p>
          <h2 className="font-display text-[clamp(28px,5vw,48px)] font-semibold text-foreground leading-[1.1]">
            Four Methods of <em className="italic font-normal text-primary">Subfloor Correction.</em>
          </h2>
          <p className="mt-3 text-[13px] md:text-[15px] font-light text-muted-foreground max-w-xl leading-relaxed">
            We approach every subfloor as carpenters and engineers. We diagnose the issue first, then prescribe the exact correction method your home needs:
          </p>
        </motion.div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {methods.map((m, i) => (
            <motion.div
              key={m.num}
              {...fade}
              transition={{ delay: i * 0.06 }}
              className="border border-border/50 rounded-2xl overflow-hidden flex flex-col"
            >
              {m.image && (
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img width={800} height={534} loading="lazy"
                    src={m.image}
                    alt={m.alt || ""}
                    className="absolute inset-0 w-full h-full object-cover grayscale-[20%] contrast-[1.1]"
                  />
                </div>
              )}
              <div className="p-6 md:p-7 flex flex-col flex-1">
                <span className="font-display text-[40px] font-semibold text-primary/20 leading-none mb-4">{m.num}</span>
                <h4 className="text-sm font-bold text-foreground mb-3 leading-snug">{m.title}</h4>
                <p className="text-[12px] md:text-[13px] font-light text-muted-foreground leading-relaxed">{m.body}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* ─── 5 · SLU SCIENCE ─── */}
    <section className="bg-[hsl(var(--neutral-950))] text-primary-foreground py-16 md:py-24 lg:py-32">
      <div className="max-w-[1200px] mx-auto px-5 md:px-12">
        <motion.div {...fade} className="max-w-3xl mx-auto">
          <p className="text-[11px] font-semibold tracking-[.18em] uppercase text-[hsl(var(--gold-400))] mb-2">Material Science</p>
          <h2 className="font-display text-[clamp(28px,5vw,48px)] font-semibold leading-[1.1] mb-6">
            The Science of SLU: <em className="italic font-normal text-[hsl(var(--gold-300))]">Why Standard Contractors Fail.</em>
          </h2>
          <p className="text-[13px] md:text-[15px] font-light text-primary-foreground/55 leading-relaxed mb-6">
            Pouring self-leveling underlayment is a chemical science, not a DIY project. Wood subfloors naturally flex, and if you use the wrong patch, it will crack and destroy your new floor. We strictly use premium, polymer-modified compounds from industry leaders like <strong className="text-primary-foreground font-medium">Schönox, Ardex, MAPEI, Bostik, and Uzin</strong>. The chemistry has to be right.
          </p>
          <div className="bg-primary-foreground/[.04] border border-primary-foreground/10 rounded-2xl p-6 md:p-8">
            <p className="text-[10px] font-bold tracking-[.14em] uppercase text-[hsl(var(--gold-400))] mb-3">The Primer Rule</p>
            <p className="text-[13px] md:text-[15px] font-light text-primary-foreground/60 leading-relaxed">
              Proper bonding requires exact primer chemistry. We specify primers based on the porosity of your concrete or the type of plywood. Ensuring the underlayment has the right emulsifiers to flex and bond permanently with your home's specific structure.
            </p>
          </div>
        </motion.div>
      </div>
    </section>

    {/* ─── 6 · PROJECT GALLERY ─── */}
    <section className="bg-background py-16 md:py-24 lg:py-32">
      <div className="max-w-[1200px] mx-auto px-5 md:px-12">
        <motion.div {...fade} className="mb-10 md:mb-14">
          <p className="text-[11px] font-semibold tracking-[.18em] uppercase text-primary mb-2">From the Field</p>
          <h2 className="font-display text-[clamp(28px,5vw,48px)] font-semibold text-foreground leading-[1.1]">
            Primer → Pour → <em className="italic font-normal text-primary">Perfection.</em>
          </h2>
          <p className="mt-3 text-[13px] md:text-[15px] font-light text-muted-foreground max-w-xl leading-relaxed">
            A complete subfloor correction sequence: specialized bonding primer over existing mastic, precision SLU pour, and the finished 8-inch hickory hardwood.
          </p>
        </motion.div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 md:gap-4">
          {galleryPhotos.map((p, i) => (
            <motion.div
              key={i}
              {...fade}
              transition={{ delay: i * 0.06 }}
              className="relative overflow-hidden rounded-md aspect-[4/5] group"
            >
              <img width={800} height={534} loading="lazy"
                src={p.img}
                alt={p.alt}
                title={p.title}
                className="absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-out grayscale-[20%] contrast-[1.1] group-hover:grayscale-0 group-hover:scale-[1.04]"
              />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-foreground/80 via-foreground/40 to-transparent p-4 pt-12 md:p-5 md:pt-16">
                <h3 className="font-display text-sm md:text-base font-semibold text-primary-foreground leading-tight">{p.caption}</h3>
                <p className="text-[10px] md:text-xs tracking-[.12em] uppercase text-primary-foreground/70 mt-1">{p.location}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* ─── LICENSED BUILDER CALLOUT ─── */}
    <FullServiceCallout variant="subfloor" />

    {/* ─── 7 · PROJECT SPOTLIGHTS ─── */}
    <ProjectSpotlights />

    {/* ─── 8 · FINAL CTA ─── */}
    <section className="bg-[hsl(var(--gold-900))] py-14 md:py-20">
      <div className="max-w-[1200px] mx-auto px-5 md:px-12 text-center">
        <motion.div {...fade}>
          <h2 className="font-display text-[clamp(26px,5vw,44px)] font-semibold text-[hsl(var(--gold-100))] leading-[1.15] mb-4">
            Don't let a bad subfloor ruin<br className="hidden sm:block" /> a $20,000 investment.
          </h2>
          <p className="text-[13px] md:text-[15px] font-light text-[hsl(var(--gold-300))]/70 max-w-lg mx-auto leading-relaxed mb-7">
            Connect directly with Michigan's NWFA Certified team to evaluate and correct your subfloor before your next flooring project.
          </p>
          <QuoteModal
            trigger={
              <button className="bg-primary-foreground text-foreground px-7 py-3.5 rounded-full text-sm font-medium hover:bg-[hsl(var(--gold-100))] transition-colors">
                Get a Project Consultation
              </button>
            }
          />
        </motion.div>
      </div>
    </section>

      <RelatedServices
        heading="The subfloor is step one. Here's step two."
        subheading="Related Services"
        links={[
          { title: "Hardwood Installation", desc: "Once your subfloor is engineered to NWFA tolerances, our installers execute the precision installation.", to: "/installation", tag: "Service" },
          { title: "Dust Control Refinishing", desc: "Existing floors on a corrected subfloor? Our 98% containment systems refinish without disrupting your home.", to: "/refinishing", tag: "Service" },
          { title: "Environment & Acclimation", desc: "Subfloor moisture is only half the equation. Learn how we ensure the wood reaches equilibrium before installation.", to: "/environment-acclimation", tag: "Technical" },
          { title: "NWFA Certified Inspections", desc: "Suspect a subfloor issue under an existing floor? Our forensic inspections identify hidden damage.", to: "/inspections", tag: "Service" },
          { title: "Armstrong Alterna", desc: "For wet areas and basements, Alterna luxury vinyl tile installs beautifully over leveled concrete subfloors.", to: "/brands/alterna", tag: "Brand" },
          { title: "Quick-Step Laminate", desc: "Quick-Step's high-pressure core is unforgiving on uneven surfaces  exactly why forensic subfloor prep matters.", to: "/brands/quick-step", tag: "Brand" },
        ]}
      />

      <GoogleReviewsCarousel contextTags={["subfloor-leveling", "installation"]} count={4} />
      <PageFAQ suppressJsonLd faqs={subfloorFAQs} heading={<>Subfloor questions <em className="italic font-normal text-primary">answered.</em></>} subheading="Subfloor Leveling FAQs" />
    </main>
    <SiteFooter />
  </div>
  );
};

export default SubfloorLeveling;
